import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Chip, LinearProgress, Skeleton } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, ArrowForward, HourglassTop } from '@mui/icons-material';
import providerService from '../../services/providerService';
import { CHECK_TAB } from '../Admin/shared/ProviderCompleteness';

// Jóváhagyásra váró szolgáltató teendőlistája a dashboardon. A tételek a backend
// profil-teljességéből jönnek (utils/providerCompleteness.js), ugyanazok, amiket az admin lát.

const colors = { dark: '#1b331f', green: '#2a5d32', muted: '#5b6e5d', gold: '#edbe5f', goldDark: '#c9932a' };


export default function ProviderOnboardingBanner({ providerId }) {
  const navigate = useNavigate();
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(!!providerId);

  useEffect(() => {
    if (!providerId) return;
    providerService.getCompleteness(providerId)
      .then(setCompleteness)
      .catch(() => setCompleteness(null))
      .finally(() => setLoading(false));
  }, [providerId]);

  const goTo = (tab) => navigate(tab ? `/profil/szolgaltato?tab=${tab}` : '/profil/szolgaltato');
  const { ready, requiredMet = 0, requiredTotal = 0, checks = [] } = completeness || {};
  // Hiányzó kötelező tételek elöl, az ajánlott (nem kötelező) a végén
  const sortedChecks = [...checks].sort((a, b) => (a.ok - b.ok) || (b.required - a.required));

  return (
    <Box
      sx={{
        mb: 4,
        p: { xs: 2.5, md: 3.5 },
        borderRadius: '20px',
        bgcolor: '#fff',
        border: '1px solid rgba(42,93,50,0.15)',
        boxShadow: '0 4px 24px rgba(27,51,31,0.08)',
      }}
    >
      {/* Fejléc: állapot + haladás */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Chip
            icon={<HourglassTop sx={{ fontSize: '16px !important', color: `${colors.dark} !important` }} />}
            label="Jóváhagyásra vár"
            size="small"
            sx={{ bgcolor: 'rgba(237,190,95,0.3)', color: colors.dark, fontWeight: 700, mb: 1 }}
          />
          <Typography
            sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, color: colors.dark, fontSize: { xs: '1.3rem', md: '1.5rem' }, lineHeight: 1.25 }}
          >
            {ready ? 'A profilja készen áll a jóváhagyásra' : 'Még néhány lépés, és megjelenhet a keresőben'}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.muted, mt: 0.5, maxWidth: 640 }}>
            {ready
              ? 'Minden szükséges adat megvan. Az adminisztrátor hamarosan átnézi a profilját, és e-mailben értesítjük.'
              : 'Az adminisztrátor a teljesen kitöltött profilokat hagyja jóvá. Pótolja az alábbiakat, a jóváhagyásról e-mailben értesítjük.'}
          </Typography>
        </Box>

        {completeness && (
          <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 220 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
              <Typography variant="body2" sx={{ color: colors.muted }}>Kitöltöttség</Typography>
              <Typography sx={{ fontWeight: 800, color: colors.dark }}>
                {requiredMet}/{requiredTotal}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={requiredTotal ? (requiredMet / requiredTotal) * 100 : 0}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(27,51,31,0.08)',
                '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: ready ? colors.green : colors.goldDark },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Teendők */}
      {loading ? (
        <Skeleton variant="rounded" height={96} sx={{ mb: 2.5 }} />
      ) : checks.length > 0 && (
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            mb: 2.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {sortedChecks.map(check => (
            <Box
              component="li"
              key={check.key}
              onClick={check.ok ? undefined : () => goTo(CHECK_TAB[check.key])}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: check.ok ? 'transparent' : 'rgba(42,93,50,0.18)',
                bgcolor: check.ok ? 'rgba(42,93,50,0.05)' : '#fff',
                cursor: check.ok ? 'default' : 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': check.ok ? {} : { borderColor: colors.green, bgcolor: 'rgba(42,93,50,0.04)' },
              }}
            >
              {check.ok
                ? <CheckCircle sx={{ fontSize: 20, color: colors.green, flexShrink: 0 }} />
                : <RadioButtonUnchecked sx={{ fontSize: 20, color: check.required ? colors.goldDark : colors.muted, flexShrink: 0 }} />}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: check.ok ? 400 : 600, color: check.ok ? colors.muted : colors.dark, lineHeight: 1.35 }}
                >
                  {check.label}
                </Typography>
                {(!check.required || (!check.ok && check.detail)) && (
                  <Typography variant="caption" sx={{ color: colors.muted }}>
                    {!check.required ? 'Ajánlott' : `Jelenleg: ${check.detail}`}
                  </Typography>
                )}
              </Box>
              {!check.ok && <ArrowForward sx={{ fontSize: 16, color: colors.green, flexShrink: 0 }} />}
            </Box>
          ))}
        </Box>
      )}

      {/* Lábléc: fő gomb + tipp */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: { xs: 1.5, sm: 2.5 } }}>
        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={() => goTo()}
          sx={{
            bgcolor: colors.dark,
            color: '#fff',
            borderRadius: '20px',
            px: 3,
            py: 1,
            fontWeight: 700,
            flexShrink: 0,
            boxShadow: 'none',
            '&:hover': { bgcolor: colors.green, boxShadow: 'none' },
          }}
        >
          Profil szerkesztése
        </Button>
        <Typography variant="body2" sx={{ color: colors.muted }}>
          Tipp: árazott <Box component="span" onClick={() => goTo(3)} sx={linkSx}>csomagokkal</Box> és
          testreszabott <Box component="span" onClick={() => goTo(1)} sx={linkSx}>ajánlatkérő űrlappal</Box> több
          megkeresést kaphat.
        </Typography>
      </Box>
    </Box>
  );
}

const linkSx = { color: colors.green, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 };
